import { Component, OnInit, OnDestroy, Input, SimpleChanges, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import { Subscription, forkJoin } from 'rxjs';
import { QuickBuild } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { Log } from 'src/app/logger.service';
import * as dayjs from 'dayjs';
import { JobFunctions } from 'src/app/shared/dw-column';

export interface CompanyData {
  company: string;
  headcount: number;
  engineering: number;
  sales: number;
  marketingProduct: number;
  arrivals: number;
  departures: number;
  netChange: number;
  currentExecs: number;
  execHires: number;
  execDepartures: number;
  activeTenure: number; // Point-in-time: average tenure of current employees
  departedTenure: number; // Over time range: average tenure of departed employees
  // Calculated fields
  turnoverRate: number;
  growthRate: number;
  engToSalesRatio: number;
  salesPct: number;
  engPct: number;
  execTurnoverRate: number;
  healthScore: number;
  // Sparkline data
  monthlyGrowthRates?: [string, number][];
  monthlyTurnoverRates?: [string, number][];
  monthlyDates?: string[];
  // Pre-computed display values
  healthScoreColor?: string;
  healthScoreBgColor?: string;
  growthRateColor?: string;
  turnoverRateColor?: string;
  execTurnoverRateColor?: string;
  activeTenureColor?: string;
  departedTenureColor?: string;
  engToSalesRatioColor?: string;
  growthSparklineOptions?: Highcharts.Options;
  turnoverSparklineOptions?: Highcharts.Options;
}

export interface SectorAverage extends CompanyData {
  isSectorAvg: true;
}

@Component({
  selector: 'app-benchmark-dashboard',
  templateUrl: './benchmark-dashboard.component.html',
  styleUrls: ['./benchmark-dashboard.component.scss'],
  standalone: false,
})
export class BenchmarkDashboardComponent implements OnInit, OnDestroy, OnChanges {
  Highcharts: typeof Highcharts = Highcharts;

  @Input() benchmarkQuickBuilds: QuickBuild[] = [];
  @Input() dateFrom: string = '';
  @Input() dateTo: string = '';
  @Input() reportTarget: string = 'moneyball';
  @Input() selectedJobFunctions: string[] | undefined = undefined;
  @Input() selectedLevels: string[] | undefined = undefined;
  @Input() selectedRoles: string[] | undefined = undefined;

  selectedCompany: string | null = null;
  companies: CompanyData[] = [];
  sectorAvg: SectorAverage = this.createEmptySectorAverage();
  isLoading: boolean = true;

  execLevels = ['C-Team', 'VP', 'Director'];

  // Table sorting
  sortBy: keyof CompanyData = 'healthScore';
  sortDir: 'asc' | 'desc' = 'desc';
  displayedColumns: string[] = [
    'healthScore',
    'company',
    'headcount',
    'engPct',
    'salesPct',
    'engToSalesRatio',
    'activeTenure',
    'growthRate',
    'turnoverRate',
    'execTurnoverRate',
    'departedTenure',
  ];

  // Cached properties to avoid recalculation
  isJobFunctionFilterActive: boolean = false;
  visibleColumns: string[] = [];
  sortedCompanies: CompanyData[] = [];
  totalHeadcount: number = 0;
  totalArrivals: number = 0;
  totalDepartures: number = 0;
  selectedCompanyData: CompanyData | null = null;
  dateRangeLabel: string = '2Y';

  // Chart options
  healthScoreChartOptions: Highcharts.Options = {};
  scatterChartOptions: Highcharts.Options = {};
  radarChartOptions: Highcharts.Options = {};

  // Update flags for Highcharts
  healthScoreUpdateFlag: boolean = false;
  scatterUpdateFlag: boolean = false;
  radarUpdateFlag: boolean = false;

  // Comparison metrics for selected company
  comparisonMetrics: any[] = [];

  // Expose Math to template
  protected readonly Math = Math;

  private subscriptions: Subscription[] = [];

  constructor(private reportService: PeopleReportService) {}

  ngOnInit() {
    // Set default date range if not provided
    if (!this.dateFrom || !this.dateTo) {
      this.dateFrom = dayjs().subtract(2, 'year').format('YYYY-MM');
      this.dateTo = dayjs().format('YYYY-MM');
    }

    // Load data if QuickBuilds are provided
    if (this.benchmarkQuickBuilds.length > 0) {
      Log.d('BenchmarkDashboardComponent: Loading data with provided QuickBuilds');
      this.loadCompanyData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['benchmarkQuickBuilds'] ||
      changes['dateFrom'] ||
      changes['dateTo'] ||
      changes['selectedJobFunctions'] ||
      changes['selectedLevels'] ||
      changes['selectedRoles']
    ) {
      Log.d('BenchmarkDashboardComponent: ngOnChanges called', changes);
      if (this.benchmarkQuickBuilds.length > 0) {
        this.loadCompanyData();
      }
      // Update job function filter state
      if (changes['selectedJobFunctions']) {
        this.updateJobFunctionFilter();
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadCompanyData() {
    Log.d('loadCompanyData called', {
      benchmarkQuickBuilds: this.benchmarkQuickBuilds,
      length: this.benchmarkQuickBuilds?.length,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    });

    if (!this.benchmarkQuickBuilds || this.benchmarkQuickBuilds.length === 0) {
      Log.w('BenchmarkDashboardComponent: No companies to benchmark');
      return;
    }

    this.isLoading = true;
    Log.d('Starting to fetch reports for', this.benchmarkQuickBuilds.length, 'companies');

    // Fetch all required reports for each company in parallel
    const allCompanyReportRequests$ = this.benchmarkQuickBuilds.map((qb) => {
      // 1. Demographics with function breakdown for dept counts
      const demographicsParams = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        group_by: ['jobs.function'],
      };
      const demographics$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-demographics`,
        'demographics',
        demographicsParams,
        this.reportTarget,
        qb
      );

      // 2. Arrivals/Departures with function breakdown
      const arrivalsParams = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        group_by: ['jobs.function'],
      };
      const arrivals$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-arrivals`,
        'arrivals_departures',
        arrivalsParams,
        this.reportTarget,
        qb
      );

      // 3. Active employee tenure (for latest month only - point in time)
      const activeTenureParams = {
        date_from: this.dateTo, // Use latest date only
        date_to: this.dateTo,
      };
      const activeTenure$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-active-tenure`,
        'tenure',
        activeTenureParams,
        this.reportTarget,
        qb
      );

      // 4. Departed employee tenure (over the time range)
      const departedTenureParams = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        status: 'departed', // Filter for departed employees only
      };
      const departedTenure$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-departed-tenure`,
        'tenure',
        departedTenureParams,
        this.reportTarget,
        qb
      );

      // 6. Executive-level arrivals/departures
      const execParams = {
        date_from: this.dateFrom,
        date_to: this.dateTo,
        group_by: ['jobs.level'],
      };
      const exec$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-exec`,
        'arrivals_departures',
        execParams,
        this.reportTarget,
        qb
      );

      // 7. Current active executives count
      const currentExecParams = {
        date_from: this.dateTo, // Use latest date to get current count
        date_to: this.dateTo,
        group_by: ['jobs.level'],
      };
      const currentExec$ = this.reportService.getReportDataWithReportDetails(
        `${qb.id}-current-exec`,
        'demographics',
        currentExecParams,
        this.reportTarget,
        qb
      );

      // Combine all reports for this company
      return forkJoin({
        demographics: demographics$,
        arrivals: arrivals$,
        activeTenure: activeTenure$,
        departedTenure: departedTenure$,
        exec: exec$,
        currentExec: currentExec$,
      });
    });

    const sub = forkJoin(allCompanyReportRequests$).subscribe({
      next: (allCompanyData) => {
        this.companies = allCompanyData.map((companyData: any, index: number) => {
          return this.calculateMetricsFromReportData(
            this.benchmarkQuickBuilds[index],
            companyData.demographics.data,
            companyData.arrivals.data,
            companyData.activeTenure.data,
            companyData.departedTenure.data,
            companyData.exec.data,
            companyData.currentExec.data
          );
        });

        this.calculateSectorAverage();

        // Pre-compute all display values for each company
        this.companies.forEach((company) => this.preComputeDisplayValues(company));

        // Update all cached properties
        this.updateCachedProperties();

        this.initializeCharts();
        this.isLoading = false;

        Log.d('BenchmarkDashboardComponent: Loaded data for companies', this.companies);
        Log.d('BenchmarkDashboardComponent: Sector average', this.sectorAvg);
        Log.d('BenchmarkDashboardComponent: Totals', {
          totalHeadcount: this.totalHeadcount,
          totalArrivals: this.totalArrivals,
          totalDepartures: this.totalDepartures,
        });

        // Mark for check since we're using OnPush
      },
      error: (error) => {
        Log.e('BenchmarkDashboardComponent: Error loading company data', error);
        this.isLoading = false;
      },
    });

    this.subscriptions.push(sub);
  }

  calculateMetricsFromReportData(
    qb: QuickBuild,
    demographicsData: any,
    arrivalsData: any,
    activeTenureData: any,
    departedTenureData: any,
    execData: any,
    currentExecData: any
  ): CompanyData {
    // Extract demographics data grouped by function
    const demographics = demographicsData.demographics || [];

    if (demographics.length === 0) {
      Log.w(`No demographics data for ${qb.name}`);
      return this.createEmptyCompanyData(qb.name);
    }

    // Find the latest date in the demographics data
    const latestDate = demographics.reduce((latest: string, entry: any) => {
      return entry.date > latest ? entry.date : latest;
    }, demographics[0].date);

    const earliestDate = demographics.reduce((earliest: string, entry: any) => {
      return entry.date < earliest ? entry.date : earliest;
    }, demographics[0].date);

    // Get all entries for the latest date and aggregate by function
    const latestEntries = demographics.filter((entry: any) => entry.date === latestDate);
    const earliestEntries = demographics.filter((entry: any) => entry.date === earliestDate);

    let headcount = 0;
    let engineering = 0;
    let sales = 0;
    let marketingProduct = 0;
    let earliestHeadcount = 0;

    // Aggregate latest data
    latestEntries.forEach((entry: any) => {
      const count = entry.count_employees || 0;
      headcount += count;

      if (entry.group_values && entry.group_values.length > 0) {
        const func = (entry.group_values[0].value || '').toLowerCase();
        if (func.includes('engineering') || func.includes('engineer')) {
          engineering += count;
        } else if (func.includes('sales') || func.includes('business development')) {
          sales += count;
        } else if (func.includes('marketing') || func.includes('product')) {
          marketingProduct += count;
        }
      }
    });

    // Calculate earliest headcount for growth rate
    earliestEntries.forEach((entry: any) => {
      earliestHeadcount += entry.count_employees || 0;
    });

    // Calculate monthly time-series data for sparklines
    const monthlyData = this.calculateMonthlyTimeSeries(
      demographics,
      arrivalsData.arrivals_departures || []
    );

    // Calculate arrivals and departures from arrivals_departures report
    const arrivalsReport = arrivalsData.arrivals_departures || [];
    let arrivals = 0;
    let departures = 0;

    arrivalsReport.forEach((entry: any) => {
      arrivals += entry.arrivals || 0;
      departures += entry.departures || 0;
    });

    const netChange = headcount - earliestHeadcount;

    // Extract executive data from exec-level arrivals/departures
    const execReport = execData.arrivals_departures || [];
    let currentExecs = 0;
    let execHires = 0;
    let execDepartures = 0;

    execReport.forEach((entry: any) => {
      if (entry.group_values && entry.group_values.length > 0) {
        if (this.execLevels.includes(entry.group_values[0].value || '')) {
          execHires += entry.arrivals || 0;
          execDepartures += entry.departures || 0;
        }
      }
    });

    // Get current active exec count from demographics report
    const currentExecReport = currentExecData.demographics || [];
    currentExecReport.forEach((entry: any) => {
      if (entry.group_values && entry.group_values.length > 0) {
        if (this.execLevels.includes(entry.group_values[0].value || '')) {
          currentExecs += entry.count_employees || 0;
        }
      }
    });

    // Extract active employee tenure (point-in-time, latest month only)
    const activeTenureReport = activeTenureData.tenure || [];
    let activeTenure = 0;

    if (activeTenureReport.length > 0) {
      let tenureSum = 0;
      let tenureCount = 0;
      activeTenureReport.forEach((entry: any) => {
        if (entry.avg_tenure) {
          tenureSum += entry.avg_tenure;
          tenureCount++;
        }
      });
      // Calculate average tenure in years (convert from months)
      activeTenure = tenureCount > 0 ? parseFloat((tenureSum / tenureCount / 12).toFixed(1)) : 0;
    }

    // Extract departed employee tenure (average over time range)
    const departedTenureReport = departedTenureData.tenure || [];
    let departedTenure = 0;

    if (departedTenureReport.length > 0) {
      let tenureSum = 0;
      let tenureCount = 0;
      departedTenureReport.forEach((entry: any) => {
        if (entry.avg_tenure) {
          tenureSum += entry.avg_tenure;
          tenureCount++;
        }
      });
      // Calculate average tenure in years (convert from months)
      departedTenure = tenureCount > 0 ? parseFloat((tenureSum / tenureCount / 12).toFixed(1)) : 0;
    }

    // Calculate time span in years for health score normalization
    const monthsInRange = dayjs(this.dateTo).diff(dayjs(this.dateFrom), 'month');
    const yearsInRange = monthsInRange / 12;

    // Calculate actual/display metrics (raw, non-annualized)
    const turnoverRate =
      departures > 0 && headcount > 0
        ? Math.round((departures / (headcount + departures / 2)) * 100)
        : 0;

    const growthRate =
      earliestHeadcount > 0 ? Math.round((netChange / earliestHeadcount) * 100) : 0;

    const engToSalesRatio = sales > 0 ? parseFloat((engineering / sales).toFixed(1)) : 0;
    const salesPct = headcount > 0 ? Math.round((sales / headcount) * 100) : 0;
    const engPct = headcount > 0 ? Math.round((engineering / headcount) * 100) : 0;

    const execTurnoverRate =
      execDepartures > 0 && currentExecs > 0
        ? Math.round((execDepartures / (currentExecs + execDepartures / 2)) * 100)
        : 0;

    // Calculate annualized metrics for health score calculation only
    const annualizedTurnoverRate =
      yearsInRange > 0 ? Math.round(turnoverRate / yearsInRange) : turnoverRate;

    const annualizedGrowthRate =
      earliestHeadcount > 0 && yearsInRange > 0
        ? Math.round((Math.pow(headcount / earliestHeadcount, 1 / yearsInRange) - 1) * 100)
        : growthRate;

    const annualizedExecTurnoverRate =
      yearsInRange > 0 ? Math.round(execTurnoverRate / yearsInRange) : execTurnoverRate;

    const healthScore = this.calculateHealthScore({
      growthRate: annualizedGrowthRate,
      turnoverRate: annualizedTurnoverRate,
      execTurnoverRate: annualizedExecTurnoverRate,
      departedTenure: activeTenure,
    });

    Log.d(`${qb.name} - Final metrics:`, {
      arrivals,
      departures,
      netChange,
      displayTurnoverRate: `${turnoverRate}%`,
      annualizedTurnoverRate: `${annualizedTurnoverRate}%`,
      displayGrowthRate: `${growthRate}%`,
      annualizedGrowthRate: `${annualizedGrowthRate}% (CAGR)`,
      displayExecTurnoverRate: `${execTurnoverRate}%`,
      annualizedExecTurnoverRate: `${annualizedExecTurnoverRate}%`,
      engPct,
      salesPct,
      activeTenure,
      yearsInRange: yearsInRange.toFixed(2),
    });

    return {
      company: qb.name,
      headcount,
      engineering,
      sales,
      marketingProduct,
      arrivals,
      departures,
      netChange,
      currentExecs,
      execHires,
      execDepartures,
      activeTenure,
      departedTenure,
      turnoverRate,
      growthRate,
      engToSalesRatio,
      salesPct,
      engPct,
      execTurnoverRate,
      healthScore,
      monthlyGrowthRates: monthlyData.growthRates,
      monthlyTurnoverRates: monthlyData.turnoverRates,
      monthlyDates: monthlyData.dates,
    };
  }

  calculateMonthlyTimeSeries(
    demographics: any[],
    arrivalsReport: any[]
  ): {
    growthRates: [string, number][];
    turnoverRates: [string, number][];
    dates: string[];
  } {
    // Group demographics by date to get monthly headcount
    const monthlyHeadcount: { [date: string]: number } = {};

    demographics.forEach((entry: any) => {
      const date = entry.date;
      if (!monthlyHeadcount[date]) {
        monthlyHeadcount[date] = 0;
      }
      monthlyHeadcount[date] += entry.count_employees || 0;
    });

    // Group arrivals/departures by date
    const monthlyArrivals: { [date: string]: number } = {};
    const monthlyDepartures: { [date: string]: number } = {};

    arrivalsReport.forEach((entry: any) => {
      const date = entry.date;
      if (!monthlyArrivals[date]) {
        monthlyArrivals[date] = 0;
        monthlyDepartures[date] = 0;
      }
      monthlyArrivals[date] += entry.arrivals || 0;
      monthlyDepartures[date] += entry.departures || 0;
    });

    // Sort dates and prepare arrays
    const dates = Object.keys(monthlyHeadcount).sort();
    const growthRates: [string, number][] = [];
    const turnoverRates: [string, number][] = [];

    // Calculate month-over-month growth and turnover rates
    for (let i = 0; i < dates.length; i++) {
      const currentDate = dates[i];
      const currentHC = monthlyHeadcount[currentDate] || 0;

      if (i === 0) {
        // First month: no previous data for growth rate
        growthRates.push([dates[i], 0]);
      } else {
        const previousDate = dates[i - 1];
        const previousHC = monthlyHeadcount[previousDate] || 0;
        const growth = previousHC > 0 ? ((currentHC - previousHC) / previousHC) * 100 : 0;
        growthRates.push([dates[i], growth]);
      }

      // Calculate turnover rate for this month
      const departures = monthlyDepartures[currentDate] || 0;
      const avgHC = currentHC > 0 ? currentHC : 1;
      const turnover = (departures / avgHC) * 100;
      turnoverRates.push([dates[i], Math.round(turnover * 10) / 10]); // Round to 1 decimal
    }

    return {
      growthRates,
      turnoverRates,
      dates,
    };
  }

  createEmptyCompanyData(companyName: string): CompanyData {
    return {
      company: companyName,
      headcount: 0,
      engineering: 0,
      sales: 0,
      marketingProduct: 0,
      arrivals: 0,
      departures: 0,
      netChange: 0,
      currentExecs: 0,
      execHires: 0,
      execDepartures: 0,
      activeTenure: 0,
      departedTenure: 0,
      turnoverRate: 0,
      growthRate: 0,
      engToSalesRatio: 0,
      salesPct: 0,
      engPct: 0,
      execTurnoverRate: 0,
      healthScore: 0,
      monthlyGrowthRates: [],
      monthlyTurnoverRates: [],
      monthlyDates: [],
    };
  }

  createEmptySectorAverage(): SectorAverage {
    return {
      company: 'Sector Average',
      headcount: 0,
      engineering: 0,
      sales: 0,
      marketingProduct: 0,
      arrivals: 0,
      departures: 0,
      netChange: 0,
      currentExecs: 0,
      execHires: 0,
      execDepartures: 0,
      activeTenure: 0,
      departedTenure: 0,
      turnoverRate: 0,
      growthRate: 0,
      engToSalesRatio: 0,
      salesPct: 0,
      engPct: 0,
      execTurnoverRate: 0,
      healthScore: 0,
      monthlyGrowthRates: [],
      monthlyTurnoverRates: [],
      monthlyDates: [],
      isSectorAvg: true,
    };
  }

  createSparklineOptions(data: any[], color: string = '#d4d4d4'): Highcharts.Options {
    return {
      exporting: {
        enabled: false,
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        width: 100,
        height: 30,
        margin: [2, 0, 2, 0],
        style: {
          overflow: 'visible',
        },
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        visible: false,
        labels: { enabled: false },
        title: { text: undefined },
        lineWidth: 0,
        tickLength: 0,
        type: 'datetime',
        dateTimeLabelFormats: {
          day: '%e %b %Y', // format for the dates on the xAxis
        },
      },
      yAxis: {
        visible: true,
        labels: { enabled: true },
        title: { text: undefined },
        gridLineWidth: 0,
        plotLines: [
          {
            value: 0,
            color: color,
            width: 1,
            zIndex: 3,
            dashStyle: 'Dash',
          },
        ],
      },
      legend: { enabled: false },
      tooltip: {
        enabled: true,
        outside: true,
        hideDelay: 100,
        pointFormatter: function (this: Highcharts.Point | any) {
          // For some reason Highcharts.Point doesn't have all the props defined in the interface
          // if this.y is >10, round to integer. if <10, round to 1 decimal place.
          if (this.y > 10) {
            return Math.round(this.y) + '%';
          } else {
            return Math.round(this.y * 10) / 10 + '%';
          }
        },
      },
      plotOptions: {
        series: {
          animation: false,
          lineWidth: 1.5,
          shadow: false,
          states: {
            hover: {
              lineWidth: 1.5,
            },
          },
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        },
      },
      series: [
        {
          type: 'line',
          data: data,
          color: color,
        },
      ],
    };
  }

  calculateHealthScore(metrics: {
    growthRate: number;
    turnoverRate: number;
    execTurnoverRate: number;
    departedTenure: number; // Parameter name kept for backwards compat, but receives activeTenure
  }): number {
    let score = 50;

    if (metrics.growthRate > 15) score += 15;
    else if (metrics.growthRate > 5) score += 10;
    else if (metrics.growthRate < 0) score -= 15;

    if (metrics.turnoverRate < 15) score += 15;
    else if (metrics.turnoverRate < 25) score += 5;
    else if (metrics.turnoverRate > 35) score -= 15;

    if (metrics.execTurnoverRate < 15) score += 10;
    else if (metrics.execTurnoverRate > 30) score -= 10;

    // Active tenure score: Higher active employee tenure = more stability
    if (metrics.departedTenure >= 3)
      score += 10; // Parameter name is departedTenure but receives activeTenure
    else if (metrics.departedTenure < 2) score -= 10;

    return Math.min(100, Math.max(0, score));
  }

  calculateSectorAverage() {
    const count = this.companies.length;

    this.sectorAvg = {
      company: 'Sector Average',
      headcount: Math.round(this.companies.reduce((s, c) => s + c.headcount, 0) / count),
      engineering: Math.round(this.companies.reduce((s, c) => s + c.engineering, 0) / count),
      sales: Math.round(this.companies.reduce((s, c) => s + c.sales, 0) / count),
      marketingProduct: Math.round(
        this.companies.reduce((s, c) => s + c.marketingProduct, 0) / count
      ),
      arrivals: Math.round(this.companies.reduce((s, c) => s + c.arrivals, 0) / count),
      departures: Math.round(this.companies.reduce((s, c) => s + c.departures, 0) / count),
      netChange: Math.round(this.companies.reduce((s, c) => s + c.netChange, 0) / count),
      currentExecs: Math.round(this.companies.reduce((s, c) => s + c.currentExecs, 0) / count),
      execHires: Math.round(this.companies.reduce((s, c) => s + c.execHires, 0) / count),
      execDepartures: Math.round(this.companies.reduce((s, c) => s + c.execDepartures, 0) / count),
      activeTenure: parseFloat(
        (this.companies.reduce((s, c) => s + c.activeTenure, 0) / count).toFixed(1)
      ),
      departedTenure: parseFloat(
        (this.companies.reduce((s, c) => s + c.departedTenure, 0) / count).toFixed(1)
      ),
      growthRate: Math.round(this.companies.reduce((s, c) => s + c.growthRate, 0) / count),
      turnoverRate: Math.round(this.companies.reduce((s, c) => s + c.turnoverRate, 0) / count),
      engPct: Math.round(this.companies.reduce((s, c) => s + c.engPct, 0) / count),
      salesPct: Math.round(this.companies.reduce((s, c) => s + c.salesPct, 0) / count),
      engToSalesRatio: parseFloat(
        (this.companies.reduce((s, c) => s + c.engToSalesRatio, 0) / count).toFixed(1)
      ),
      execTurnoverRate: Math.round(
        this.companies.reduce((s, c) => s + c.execTurnoverRate, 0) / count
      ),
      healthScore: Math.round(this.companies.reduce((s, c) => s + c.healthScore, 0) / count),
      isSectorAvg: true,
    };
  }

  initializeCharts() {
    this.updateHealthScoreChart();
    this.updateScatterChart();
    if (this.selectedCompany) {
      this.updateRadarChart();
      this.updateComparisonMetrics();
    }
  }

  onCompanySelect(companyName: string | null) {
    this.selectedCompany = companyName;
    this.updateSelectedCompanyData();

    // Only update the radar chart and comparison metrics for selected company
    // Don't update the health score chart or scatter chart
    if (this.selectedCompany) {
      this.updateRadarChart();
      this.updateComparisonMetrics();
    }
  }

  getScoreColor(score: number): string {
    if (score >= 75) return '#10B981';
    if (score >= 60) return '#14B8A6';
    if (score >= 45) return '#F59E0B';
    return '#EF4444';
  }

  getComparisonColor(value: number, sectorValue: number, inverse: boolean = false): string {
    if (inverse) return value <= sectorValue ? '#10B981' : '#EF4444';
    return value >= sectorValue ? '#10B981' : '#EF4444';
  }

  handleSort(field: keyof CompanyData) {
    if (this.sortBy === field) {
      this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortBy = field;
      this.sortDir = 'desc';
    }
    this.updateSortedCompanies();
  }

  // Helper methods to update cached properties
  private updateCachedProperties() {
    this.updateJobFunctionFilter();
    this.updateSortedCompanies();
    this.updateTotals();
    this.updateDateRangeLabel();
    this.updateSelectedCompanyData();
  }

  private updateJobFunctionFilter() {
    const allJobFunctions = Object.values(JobFunctions);
    this.isJobFunctionFilterActive = !!(
      this.selectedJobFunctions &&
      this.selectedJobFunctions.length > 0 &&
      this.selectedJobFunctions.length < allJobFunctions.length
    );

    if (this.isJobFunctionFilterActive) {
      this.visibleColumns = this.displayedColumns.filter(
        (col) => !['engPct', 'salesPct', 'engToSalesRatio'].includes(col)
      );
    } else {
      this.visibleColumns = this.displayedColumns;
    }
  }

  private updateSortedCompanies() {
    this.sortedCompanies = [...this.companies].sort((a, b) => {
      const mult = this.sortDir === 'desc' ? -1 : 1;
      if (this.sortBy === 'company') return a.company.localeCompare(b.company) * mult * -1;
      return ((a[this.sortBy] as number) - (b[this.sortBy] as number)) * mult;
    });
  }

  private updateTotals() {
    this.totalHeadcount = this.companies.reduce((sum, c) => sum + c.headcount, 0);
    this.totalArrivals = this.companies.reduce((sum, c) => sum + c.arrivals, 0);
    this.totalDepartures = this.companies.reduce((sum, c) => sum + c.departures, 0);
  }

  private updateSelectedCompanyData() {
    if (!this.selectedCompany) {
      this.selectedCompanyData = null;
      return;
    }
    this.selectedCompanyData =
      this.companies.find((c) => c.company === this.selectedCompany) || null;
  }

  private updateDateRangeLabel() {
    if (!this.dateFrom || !this.dateTo) {
      this.dateRangeLabel = '2Y';
      return;
    }

    const from = dayjs(this.dateFrom);
    const to = dayjs(this.dateTo);
    const months = to.diff(from, 'month');
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years >= 1 && remainingMonths === 0) {
      this.dateRangeLabel = `${years}Y`;
    } else if (years >= 1) {
      this.dateRangeLabel = `${years}Y ${remainingMonths}M`;
    } else {
      this.dateRangeLabel = `${months}M`;
    }
  }

  private preComputeDisplayValues(company: CompanyData) {
    // Pre-compute health score colors
    company.healthScoreColor = this.getScoreColor(company.healthScore);
    company.healthScoreBgColor = company.healthScoreColor + '20';

    // Pre-compute comparison colors
    company.growthRateColor = this.getComparisonColor(
      company.growthRate,
      this.sectorAvg.growthRate
    );
    company.turnoverRateColor = this.getComparisonColor(
      company.turnoverRate,
      this.sectorAvg.turnoverRate,
      true
    );
    company.execTurnoverRateColor = this.getComparisonColor(
      company.execTurnoverRate,
      this.sectorAvg.execTurnoverRate,
      true
    );
    company.activeTenureColor = this.getComparisonColor(
      company.activeTenure,
      this.sectorAvg.activeTenure
    );
    company.departedTenureColor = this.getComparisonColor(
      company.departedTenure,
      this.sectorAvg.departedTenure
    );
    company.engToSalesRatioColor =
      company.engToSalesRatio >= this.sectorAvg.engToSalesRatio ? '#10B981' : '#F59E0B';

    // Pre-compute sparkline chart options
    if (company.monthlyGrowthRates && company.monthlyGrowthRates.length > 0) {
      company.growthSparklineOptions = this.createSparklineOptions(company.monthlyGrowthRates);
    }
    if (company.monthlyTurnoverRates && company.monthlyTurnoverRates.length > 0) {
      company.turnoverSparklineOptions = this.createSparklineOptions(company.monthlyTurnoverRates);
    }
  }

  updateHealthScoreChart() {
    // Always show all companies in the health score chart
    const data = this.sortedCompanies.map((c) => ({
      name: c.company,
      y: c.healthScore,
      color: this.getScoreColor(c.healthScore),
    }));

    const options: Highcharts.Options = {
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        height: 340,
      },
      title: {
        text: 'Workforce Health Score',
        style: { color: '#fff', fontSize: '16px', fontWeight: '700' },
      },
      subtitle: {
        text: `Composite score • Dashed line = sector average (${this.sectorAvg.healthScore})`,
        style: { color: '#64748B', fontSize: '12px' },
      },
      xAxis: {
        categories: data.map((d) => d.name),
        labels: { style: { color: '#E2E8F0', fontSize: '11px' } },
        lineColor: 'transparent',
        tickLength: 0,
      },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: '' },
        labels: { style: { color: '#64748B', fontSize: '11px' } },
        gridLineColor: 'rgba(255,255,255,0.05)',
        plotLines: [
          {
            value: this.sectorAvg.healthScore,
            color: '#64748B',
            width: 2,
            dashStyle: 'Dash',
            zIndex: 5,
          },
        ],
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          borderRadius: 6,
          dataLabels: {
            enabled: true,
            format: '{y}',
            style: { color: '#fff', fontSize: '11px', fontWeight: '600' },
          },
        },
      },
      series: [
        {
          type: 'bar',
          name: 'Health Score',
          data: data,
        },
      ],
      credits: { enabled: false },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        borderColor: 'rgba(255,255,255,0.1)',
        style: { color: '#fff' },
      },
    };

    this.healthScoreChartOptions = options;
    // Toggle flag to trigger re-render
    this.healthScoreUpdateFlag = !this.healthScoreUpdateFlag;
  }

  updateScatterChart() {
    const options: Highcharts.Options = {
      chart: {
        type: 'scatter',
        backgroundColor: 'transparent',
        height: 340,
      },
      title: {
        text: 'Growth vs. Turnover',
        style: { color: '#fff', fontSize: '16px', fontWeight: '700' },
      },
      subtitle: {
        text: 'Ideal: high growth (right), low turnover (bottom) • Lines = sector avg',
        style: { color: '#64748B', fontSize: '12px' },
      },
      xAxis: {
        title: { text: 'Growth %', style: { color: '#64748B', fontSize: '11px' } },
        labels: { style: { color: '#64748B', fontSize: '11px' } },
        gridLineColor: 'rgba(255,255,255,0.05)',
        plotLines: [
          {
            value: this.sectorAvg.growthRate,
            color: '#14B8A6',
            width: 1,
            dashStyle: 'Dash',
            zIndex: 3,
          },
        ],
      },
      yAxis: {
        title: { text: 'Turnover %', style: { color: '#64748B', fontSize: '11px' } },
        labels: { style: { color: '#64748B', fontSize: '11px' } },
        gridLineColor: 'rgba(255,255,255,0.05)',
        plotLines: [
          {
            value: this.sectorAvg.turnoverRate,
            color: '#F59E0B',
            width: 1,
            dashStyle: 'Dash',
            zIndex: 3,
          },
        ],
      },
      legend: { enabled: false },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: {
              hover: {
                enabled: true,
                lineColor: 'rgb(100,100,100)',
              },
            },
          },
        },
      },
      series: [
        {
          type: 'scatter',
          name: 'Companies',
          data: this.companies.map((c) => ({
            x: c.growthRate,
            y: c.turnoverRate,
            name: c.company,
            color:
              c.growthRate < 0
                ? '#EF4444'
                : c.turnoverRate > this.sectorAvg.turnoverRate
                  ? '#F59E0B'
                  : '#10B981',
          })),
        },
      ],
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        borderColor: 'rgba(255,255,255,0.1)',
        style: { color: '#fff' },
        useHTML: true,
        formatter: function (this: any) {
          return `<div style="padding: 12px;">
            <p style="color: #fff; font-weight: 700; margin: 0 0 6px;">${this.point.name}</p>
            <p style="color: #10B981; font-size: 12px; margin: 2px 0;">Growth: <strong>${this.x}%</strong></p>
            <p style="color: #EF4444; font-size: 12px; margin: 2px 0;">Turnover: <strong>${this.y}%</strong></p>
          </div>`;
        },
      },
      credits: { enabled: false },
    };

    this.scatterChartOptions = options;
    // Toggle flag to trigger re-render
    this.scatterUpdateFlag = !this.scatterUpdateFlag;
  }

  updateRadarChart() {
    if (!this.selectedCompanyData) return;

    const categories = ['Growth', 'Retention', 'Exec Stability', 'Active Tenure', 'Health Score'];

    const companyData = [
      Math.min(100, Math.max(0, (this.selectedCompanyData.growthRate + 10) * 2)),
      Math.max(0, 100 - this.selectedCompanyData.turnoverRate * 2),
      Math.max(0, 100 - this.selectedCompanyData.execTurnoverRate * 2),
      Math.min(100, (this.selectedCompanyData.activeTenure / 10) * 100), // Normalize active tenure: 10 years = 100
      this.selectedCompanyData.healthScore,
    ];

    const sectorData = [
      Math.min(100, Math.max(0, (this.sectorAvg.growthRate + 10) * 2)),
      Math.max(0, 100 - this.sectorAvg.turnoverRate * 2),
      Math.max(0, 100 - this.sectorAvg.execTurnoverRate * 2),
      Math.min(100, (this.sectorAvg.activeTenure / 10) * 100), // Normalize active tenure: 10 years = 100
      this.sectorAvg.healthScore,
    ];

    const options: Highcharts.Options = {
      chart: {
        polar: true,
        backgroundColor: 'transparent',
        height: 320,
      },
      title: {
        text: 'Performance Profile',
        style: { color: '#fff', fontSize: '16px', fontWeight: '700' },
      },
      subtitle: {
        text: 'Multi-dimensional comparison • Outer edge = better',
        style: { color: '#64748B', fontSize: '12px' },
      },
      pane: {
        size: '80%',
      },
      xAxis: {
        categories: categories,
        labels: {
          style: { color: '#94A3B8', fontSize: '11px' },
        },
        lineColor: 'rgba(255,255,255,0.1)',
      },
      yAxis: {
        min: 0,
        max: 100,
        labels: { enabled: false },
        gridLineColor: 'rgba(255,255,255,0.1)',
      },
      legend: {
        enabled: true,
        itemStyle: { color: '#94A3B8', fontSize: '12px' },
      },
      series: [
        {
          type: 'line',
          name: this.selectedCompany!,
          data: companyData,
          color: '#14B8A6',
          lineWidth: 2,
        },
        {
          type: 'line',
          name: 'Sector Avg',
          data: sectorData,
          color: '#64748B',
          lineWidth: 2,
          dashStyle: 'Dash',
        },
      ],
      credits: { enabled: false },
    };

    this.radarChartOptions = options;
    // Toggle flag to trigger re-render
    this.radarUpdateFlag = !this.radarUpdateFlag;
  }

  updateComparisonMetrics() {
    if (!this.selectedCompanyData) {
      Log.d('updateComparisonMetrics: No selectedCompanyData, returning');
      return;
    }

    const createMetric = (
      metric: string,
      company: number,
      sector: number,
      unit: string,
      better: 'higher' | 'lower'
    ) => {
      const maxVal = Math.max(company, sector) * 1.2;
      const isCompanyBetter = better === 'higher' ? company >= sector : company <= sector;
      return {
        metric,
        company,
        sector,
        unit,
        better,
        isCompanyBetter,
        companyBarWidth: (company / maxVal) * 100,
        sectorBarWidth: (sector / maxVal) * 100,
        companyBarColor: isCompanyBetter ? '#3B82F6' : 'rgba(59, 130, 246, 0.5)',
        companyValueColor: isCompanyBetter ? '#10B981' : '#EF4444',
      };
    };

    const metrics = [
      createMetric(
        'Health Score',
        this.selectedCompanyData.healthScore,
        this.sectorAvg.healthScore,
        '',
        'higher'
      ),
      createMetric(
        'Growth Rate',
        this.selectedCompanyData.growthRate,
        this.sectorAvg.growthRate,
        '%',
        'higher'
      ),
      createMetric(
        'Turnover Rate',
        this.selectedCompanyData.turnoverRate,
        this.sectorAvg.turnoverRate,
        '%',
        'lower'
      ),
      createMetric(
        'Exec Turnover',
        this.selectedCompanyData.execTurnoverRate,
        this.sectorAvg.execTurnoverRate,
        '%',
        'lower'
      ),
      createMetric(
        'Active Tenure',
        this.selectedCompanyData.activeTenure,
        this.sectorAvg.activeTenure,
        ' yrs',
        'higher'
      ),
      createMetric(
        'Departed Tenure',
        this.selectedCompanyData.departedTenure,
        this.sectorAvg.departedTenure,
        ' yrs',
        'higher'
      ),
    ];

    // Only add Eng:Sales ratio if job function filter is not active
    if (!this.isJobFunctionFilterActive) {
      metrics.push(
        createMetric(
          'Eng:Sales Ratio',
          this.selectedCompanyData.engToSalesRatio,
          this.sectorAvg.engToSalesRatio,
          'x',
          'higher'
        )
      );
    }

    this.comparisonMetrics = metrics;
  }
}
